import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Trash2, UserPlus } from "lucide-react";
import { adminApi } from "@/lib/blogApi";
import { formatDate } from "@/components/blog/shared";

export default function EditorsPanel({ token, currentUserId }) {
  const [staff, setStaff] = useState([]);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [creating, setCreating] = useState(false);

  const load = useCallback(() => {
    adminApi.listEditors(token).then(setStaff).catch(() => toast.error("Failed to load team"));
  }, [token]);

  useEffect(() => {
    load();
  }, [load]);

  const create = async (e) => {
    e.preventDefault();
    if (password.length < 8) return toast.error("Password must be at least 8 characters");
    setCreating(true);
    try {
      await adminApi.createEditor(token, { email, password });
      toast.success(`Editor account created for ${email}`);
      setEmail("");
      setPassword("");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not create editor");
    } finally {
      setCreating(false);
    }
  };

  const remove = async (member) => {
    if (!window.confirm(`Remove editor ${member.email}? They will lose all access.`)) return;
    try {
      await adminApi.deleteEditor(token, member.user_id);
      toast.success("Editor removed");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not remove editor");
    }
  };

  return (
    <div className="grid lg:grid-cols-2 gap-8" data-testid="editors-panel">
      <div>
        <h2 className="font-heading text-lg font-bold text-white mb-6">Team members</h2>
        <div className="border border-[#27272A] divide-y divide-[#27272A]">
          {staff.map((m) => (
            <div key={m.user_id} className="p-4 flex items-center justify-between bg-[#0A0A0B]" data-testid={`team-member-${m.email}`}>
              <div>
                <p className="text-white text-sm">{m.email} {m.user_id === currentUserId && <span className="text-xs text-[#A1A1AA]">(you)</span>}</p>
                <p className="text-xs text-[#A1A1AA] mt-0.5">
                  <span className={m.role === "admin" ? "text-[#A855F7] uppercase" : "uppercase"}>{m.role}</span> · joined {formatDate(m.created_at)}
                </p>
              </div>
              {m.role === "editor" && (
                <button className="p-2 text-[#A1A1AA] hover:text-red-400" onClick={() => remove(m)} data-testid={`delete-editor-${m.email}`} title="Remove editor">
                  <Trash2 size={16} />
                </button>
              )}
            </div>
          ))}
        </div>
      </div>

      <div>
        <h2 className="font-heading text-lg font-bold text-white mb-6">Add an editor</h2>
        <form onSubmit={create} className="border border-[#27272A] p-6 space-y-4 bg-[#0A0A0B]" data-testid="create-editor-form">
          <div>
            <label className="admin-label">Email</label>
            <input type="email" className="admin-input" value={email} onChange={(e) => setEmail(e.target.value)} required data-testid="new-editor-email" />
          </div>
          <div>
            <label className="admin-label">Password (min 8 characters)</label>
            <input type="text" className="admin-input" value={password} onChange={(e) => setPassword(e.target.value)} required minLength={8} data-testid="new-editor-password" />
          </div>
          <p className="text-xs text-[#A1A1AA]">Editors can create, edit, preview, publish and unpublish articles. Only admins can delete articles and manage the team.</p>
          <button type="submit" disabled={creating} className="btn-primary px-5 py-2.5 text-sm font-semibold inline-flex items-center gap-2" data-testid="create-editor-submit">
            <UserPlus size={15} /> {creating ? "Creating…" : "Create Editor"}
          </button>
        </form>
      </div>
    </div>
  );
}
